import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  useGetSavedCardsQuery,
  usePlaceOrderMutation,
} from '@/components/orders/services/orderApi';
import type { SavedCard } from '@/types/order.types';
import type { ImageAssetType, ServiceType } from '@/components/ui/carousel';
import { buildAssetOrder } from './orderServicePayloads';
import { useSkipCheckout } from '@/hooks/useEntityScope';

export function useAssetOrderPayment() {
  const dispatch = useDispatch();
  const { data: cardsResponse, isLoading, refetch } = useGetSavedCardsQuery();
  const [placeOrder] = usePlaceOrderMutation();

  const cards = useMemo<SavedCard[]>(() => cardsResponse?.cards ?? [], [cardsResponse]);
  const [selectedCard, setSelectedCard] = useState<SavedCard | null>(null);
  const [placing, setPlacing] = useState(false);

  // Mirror the isLogin guard used by usePayment/usePlaceOrder: skipCheckout is
  // only meaningful for an authenticated user.
  const isLogin = localStorage.getItem('isLogin');
  const skipCheckout = useSkipCheckout();
  const isSkipCheckout = isLogin === null ? false : skipCheckout;

  useEffect(() => {
    if (!cards.length) return;
    // Pre-select a card so the modal can offer one-tap pay: the flagged default
    // if there is one, else the first card (the user can still Change it).
    setSelectedCard((prev) => prev ?? cards.find((c) => c.default) ?? cards[0]);
  }, [cards]);

  const placeOrderNow = async ({
    photos,
    service,
    note,
    propertyAddress,
    total,
    promoCodeId,
  }: {
    photos: ImageAssetType[];
    service: ServiceType;
    note: string;
    propertyAddress: string;
    total: number;
    promoCodeId?: string | null;
  }): Promise<string | undefined> => {
    const isFree = total === 0;
    // Card required only when there's something to charge and the account isn't
    // on monthly invoice. Free + skipCheckout both place without a card.
    const card = isFree || isSkipCheckout ? null : selectedCard;
    if (!isFree && !isSkipCheckout && !card) {
      dispatch(showAlert({ type: Variants.ERROR, message: 'Select a payment method' }));
      return undefined;
    }
    setPlacing(true);
    try {
      const order = buildAssetOrder(photos, service, note, propertyAddress, card, promoCodeId);
      const res = await placeOrder(order).unwrap();
      return res.orderNumber;
    } catch (error) {
      console.error('Error placing asset order:', error);
      dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to place order' }));
      throw error;
    } finally {
      setPlacing(false);
    }
  };

  return {
    cards,
    isLoading,
    selectedCard,
    setSelectedCard,
    placing,
    isSkipCheckout,
    refetchCards: refetch,
    placeOrderNow,
  };
}
